import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import {
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	JurisdictionPrefixPipe,
	ResponsibleGamingConfigService,
	SportsbookBlockSelector,
} from "@obg/ngx.core";
import { TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { RouteAction } from "@obg/ngx.router";
import { ignoreNil, ignoreTruthy } from "@obg/rxjs";
import { combineLatest, Observable } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";

import { AlertButtonColor, AlertButtonStyle } from "../../alert/alert.model";
import { TranslatableAlertService } from "../../alert/translatable-alert.service";

const GAME_BLOCK_DIALOG_ID = "game-block";

@Injectable({
	providedIn: "root",
})
export class SportsbookBlockService {
	constructor(
		private translatableAlertService: TranslatableAlertService,
		private store: Store<CoreState>,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe,
		private config: ResponsibleGamingConfigService,
		private translationSelector: TranslationSelector,
		private customerSupportService: CustomerSupportService
	) {}

	checkSportsbookBlocked$(): Observable<boolean> {
		return combineLatest([
			this.store.select(AuthSelector.isAuthenticated),
			this.store.select(SportsbookBlockSelector.isSportsbookBlocked).pipe(ignoreNil()),
		]).pipe(
			filter(
				([isAuthenticated, isSportsbookBlocked]) =>
					isAuthenticated && !!isSportsbookBlocked && this.config.getCasinoBlockEnabled()
			),
			switchMap(() => this.openGameBlockDialog$())
		);
	}

	private openGameBlockDialog$(): Observable<any> {
		return this.store.pipe(
			select(this.translationSelector.isBusy()),
			ignoreTruthy(),
			take(1),
			switchMap(() =>
				this.translatableAlertService.open$(
					{
						id: GAME_BLOCK_DIALOG_ID,
						testId: GAME_BLOCK_DIALOG_ID,
						title: this.jurisdictionPrefixPipe.transform("product-block.sportsbook-block.title"),
						message: this.jurisdictionPrefixPipe.transform("product-block.sportsbook-block.description"),
						dialogConfig: {
							closeOnNavigation: true,
							autoFocus: false,
						},
						buttons: [
							{
								style: AlertButtonStyle.Stroked,
								color: AlertButtonColor.Primary,
								text: "general.customer-support",
								handler: () => this.customerSupportService.openSupportPageInNewTab(),
							},
							{
								text: "general.product.casino",
								handler: () => this.store.dispatch(RouteAction.navigate(CORE_ROUTE.lobbies.casino)),
							},
						],
					},
					[TranslationGroup.Sportsbook]
				)
			)
		);
	}
}
